import type { TodayView } from '@aj/core';
import { HStack, StatPill, Text, VStack } from '@aj/ui';

import { useI18n, useT } from '../../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

export type HeaderProps = {
  view: TodayView;
  onOpenProfile: () => void;
  onOpenProgress: () => void;
  className?: string;
};

/**
 * Greeting and the kept line on the left, the two numbers that carry the
 * identity on the right.
 *
 * Both numbers are HIDDEN at zero. A ⚡0 and a 🔥0 on day one is the worst
 * thing a new student can see — the top row should be empty until there is
 * something to be proud of.
 *
 * A broken streak is the one exception to "hide at zero": it falls back to the
 * best run ever, under a trophy rather than a flame. The run happened. An app
 * whose entire promise is "here's the evidence" cannot be the thing that
 * deletes it the morning after one missed day.
 */
export function Header({ view, onOpenProfile, onOpenProgress, className }: HeaderProps) {
  const t = useT();
  const i18n = useI18n();

  const greeting = t(`greeting.${view.greeting}` as MessageKey);
  const line = view.name ? t('home.greetingWith', { greeting, name: view.name }) : greeting;

  const kept = view.commitment;
  const live = view.streak.alive && view.streak.current > 0;
  const showBest = !live && view.streak.best > 0;

  return (
    <HStack gap={3} align="center" justify="between" className={className}>
      <VStack gap={0} className="min-w-0">
        <Text as="h1" variant="heading" tone="ink" className="min-w-0 truncate">
          {line}
        </Text>
        {kept && (
          <Text variant="caption" tone="muted" className="min-w-0 truncate">
            {t('home.kept', {
              percent: t('home.percent', { value: Math.round(kept.ratio * 100) }),
              count: kept.days,
            })}
          </Text>
        )}
      </VStack>

      <HStack gap={2} align="center" className="shrink-0">
        {view.power > 0 && (
          <StatPill
            icon="bolt"
            tone="gold"
            value={i18n.formatNumber(view.power)}
            label={t('power.aria', { value: view.power })}
            onClick={onOpenProfile}
          />
        )}
        {live && (
          <StatPill
            icon="flame"
            tone="flame"
            value={i18n.formatNumber(view.streak.current)}
            label={t('streak.aria', { count: view.streak.current })}
            onClick={onOpenProgress}
          />
        )}
        {showBest && (
          <StatPill
            icon="trophy"
            tone="accent"
            value={i18n.formatNumber(view.streak.best)}
            label={t('streak.bestAria', { count: view.streak.best })}
            onClick={onOpenProgress}
          />
        )}
      </HStack>
    </HStack>
  );
}
